import type { GetContractInformationResponse } from '@/models/api/get-contract-information-response'
import { getContractInformationResponseSchema } from '@/models/api/get-contract-information-response'
import {
  myAccountGetAccountInformationSchema,
  type MyAccountGetAccountInformation
} from '@/models/api/my-account-get-account-information'
import {
  administratorInformationSchema,
  type AdministratorInformation
} from '@/models/api/my-account-get-administrator-information'
import {
  myAccountGetCorporateInformationSchema,
  type MyAccountGetCorporateInformation
} from '@/models/api/my-account-get-corporate-information'

import {
  myAccountGetDownloadFileSchema,
  type MyAccountGetDownloadFile
} from '@/models/api/my-account-get-download-file'
import type { MyAccountGetShoppingCart } from '@/models/api/my-account-get-shopping-cart'
import { myAccountGetShoppingCartSchema } from '@/models/api/my-account-get-shopping-cart'
import {
  myAccountRemindItemSchema,
  type MyAccountRemindItem
} from '@/models/api/my-account-remind-item'
import type { MyAccountRemindItemDelete } from '@/models/api/my-account-remind-item-delete'
import type { MyAccountRemindItemPin } from '@/models/api/my-account-remind-item-pin'
import type { MyAccountRemindItemUpdateRequest } from '@/models/api/my-account-remind-item-update-request'
import {
  myAccountSubscribeNewsletterSchema,
  type MyAccountSubscribeNewsletter
} from '@/models/api/my-account-subscribe-newsletter'
import type { MyAccountUpdateAccountInformationRequest } from '@/models/api/my-account-update-account-information-request'
import type { RemindListRequest } from '@/models/api/remind-list-request'
import {
  unReadRemindItemSchema,
  type UnReadRemindItem
} from '@/models/api/un-read-remind-item'
import type { Identity } from '~library/enums'
import {
  apiValidator,
  type ApiFactory,
  type ApiRequestConfig,
  type ApiResponse
} from '~library/services'

export default class MyAccountApi {
  constructor(private api: ApiFactory) {}

  /**
   * API 取得提醒事項
   * https://hackmd.io/4tpTOkhBSdK3FxPS49nt9Q
   */
  @apiValidator(myAccountRemindItemSchema.array())
  getRemindList(
    data: RemindListRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountRemindItem[]>> {
    return this.api.post('MyAccount/RemindItem', data, config)
  }

  /**
   * API 已讀提醒事項
   * https://hackmd.io/5I6QBctcST2E34RNR4wLwQ
   */
  updateRemindData(
    data: MyAccountRemindItemUpdateRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData('MyAccount/RemindItem', data, config)
  }

  /**
   * API 刪除提醒事項
   * https://hackmd.io/AQ5oApH5Stmf7_z_z7sPYA
   */
  deleteRemindData(data: MyAccountRemindItemDelete): Promise<ApiResponse> {
    return this.api.post('MyAccount/DeleteRemindItem', data)
  }

  /**
   * API 取得管理者資訊
   * https://hackmd.io/8gB6D2XaS8m-bqpUKG1p4g
   */
  @apiValidator(administratorInformationSchema)
  getAdministratorInformation(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<AdministratorInformation>> {
    return this.api.get('MyAccount/GetAdministratorInformation', config)
  }

  /**
   * API 取得公司資料
   * https://hackmd.io/fLvMchuzTUOgL72ldavmow
   */
  @apiValidator(myAccountGetCorporateInformationSchema)
  getCorporateInformation(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountGetCorporateInformation>> {
    return this.api.get('MyAccount/GetCorporateInformation', config)
  }

  /**
   * API 取得下載專區檔案清單
   * https://hackmd.io/YvwS81X4Req9FUn8XF_vFg
   */
  @apiValidator(myAccountGetDownloadFileSchema.array())
  getDownloadFileGetList(
    identity: Identity,
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountGetDownloadFile[]>> {
    return this.api.get(`MyAccount/GetDownloadFileList/${identity}`, config)
  }

  /**
   * API 取得下載專區檔案
   * https://hackmd.io/Ix-Y3R_fRBG9_y61LHMPRQ
   */
  getDownloadFile(fileId: string): Promise<void> {
    return this.api.downloadFile({
      method: 'POST',
      url: `MyAccount/GetDownloadFile/${fileId}`
    })
  }

  /**
   * API 更新 訂閱/取消電子報 - 公司資料
   * https://hackmd.io/7bF5sY-jTp-DV13MgmNMXg
   */
  subscribeNewsletterUpdateData(
    data: MyAccountSubscribeNewsletter,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData('MyAccount/SubscribeNewsletter', data, config)
  }

  /**
   * API 取得 訂閱/取消電子報 - 公司資料
   * https://hackmd.io/K7x2pckQQFSJHh3OezAOMA
   */
  @apiValidator(myAccountSubscribeNewsletterSchema)
  getSubscribeNewsletter(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountSubscribeNewsletter>> {
    return this.api.get('MyAccount/GetSubscribeNewsletter', config)
  }

  /**
   * API 取得購物車清單
   * https://hackmd.io/3qNNwCf8S9CMYsIJXGAelQ
   */
  @apiValidator(myAccountGetShoppingCartSchema)
  getShoppingCart(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountGetShoppingCart>> {
    return this.api.get('MyAccount/GetShoppingCart', config)
  }

  /**
   * API 取得帳號資訊
   * https://hackmd.io/t1eKDynsS46FMrUP7Vtisg
   */
  @apiValidator(myAccountGetAccountInformationSchema)
  getAccountInformation(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<MyAccountGetAccountInformation>> {
    return this.api.get('MyAccount/GetAccountInformation', config)
  }

  /**
   * API 更新帳號資訊
   * https://hackmd.io/iAesha4VQYGF0Hx3jDIpAw
   */
  updateAccountInformationUpdateData(
    data: MyAccountUpdateAccountInformationRequest,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData(
      'MyAccount/UpdateAccountInformation',
      data,
      config
    )
  }

  /**
   * API 解除企業綁定
   * https://hackmd.io/R6Xr4srmTdm_NfMc5oVn0Q
   */
  updateUnbindMemberCardNumberUpdateData(
    memberCardNumber: string,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData(`MyAccount/Unbind/${memberCardNumber}`, config)
  }

  /**
   * API 取得未讀的提醒事項數量
   * https://hackmd.io/TSR-dj7wTDSoLTqjZPiVrg
   */
  @apiValidator(unReadRemindItemSchema)
  getUnReadRemindItem(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<UnReadRemindItem>> {
    return this.api.get('MyAccount/GetUnReadRemindItem', config)
  }

  /**
   * API 修改提醒事項釘選
   * https://hackmd.io/9QCVBCpvRI-Qin22HxrSZg
   */
  updatePinRemindData(
    data: MyAccountRemindItemPin,
    config?: ApiRequestConfig
  ): Promise<ApiResponse> {
    return this.api.updateData('MyAccount/RemindItemPin', data, config)
  }

  /**
   * API 取得企業目前合約資訊
   * https://hackmd.io/rUmAoQ9WSkuU5XcCIBjfGA
   */
  @apiValidator(getContractInformationResponseSchema)
  getContractInformation(
    config?: ApiRequestConfig
  ): Promise<ApiResponse<GetContractInformationResponse>> {
    return this.api.get('MyAccount/GetContractInformation', config)
  }
}
